import React, { useEffect, useState } from "react";
import { Autocomplete, Button, CircularProgress, Paper, TextField } from "@mui/material";

import "./ImportFolder.styles.scss";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";

import { useImportContextHook } from "./use-import-context-hook";
import { IFolderTemplate, ITemplate } from "store/types";
import { ImportContext } from "./import-context";
import { FolderNode } from "./FolderNode";
import { useDealActions } from "store";
import { useActionHook } from "hooks";
import { Loading } from "../..";

interface ImportFolderProps {
  closeSlider: () => void;
}

export const ImportFolder: React.FC<ImportFolderProps> = ({ closeSlider }) => {
  const [templateValue, setTemplateValue] = useState<ITemplate>();
  const importContext = useImportContextHook(templateValue as ITemplate);
  const { importLoading, state, onSubmit } = importContext;

  const { getTemplatesAction } = useDealActions();
  const { action: getTemplates, loading, data: templates = [] } = useActionHook(getTemplatesAction);

  useEffect(() => {
    getTemplates(true);
  }, []);

  return (
    <div className="import-folder">
      <ImportContext.Provider value={importContext}>
        <div className="import-folder__head">
          <span className="import-folder__title">Import folder</span>
          <IconButton onClick={closeSlider}>
            <CloseIcon className="import-folder__head-icon" />
          </IconButton>
        </div>
        <div className="import-folder__body">
          <div className="import-folder__template">
            <div className="import-folder__template-selector">
              {loading ? (
                <div className="import-folder__spinner">
                  <CircularProgress size={200} />
                </div>
              ) : (
                <Autocomplete
                  disablePortal
                  options={templates}
                  PaperComponent={({ children }) => {
                    return (
                      <Paper
                        sx={{
                          position: "absolute",
                          maxWidth: "370px",
                          backgroundColor: "white",
                          borderRadius: "10px",
                          "& .MuiAutocomplete-option:hover": {
                            backgroundColor: "#edf1f9"
                          }
                        }}
                      >
                        <>{children}</>
                      </Paper>
                    );
                  }}
                  renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ITemplate) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.title}
                      </li>
                    );
                  }}
                  getOptionLabel={(option) => option.title}
                  onChange={(_, value: ITemplate) => {
                    setTemplateValue(value);
                  }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField {...params} label="Template Name" sx={{ marginTop: "40px", width: "370px" }} />
                  )}
                />
              )}
            </div>
          </div>
          {templateValue ? (
            <div className="import-folder__list">
              <span className="import-folder__list-title">Select folders and files to import</span>
              {templateValue?.sections
                ?.filter((folder) => folder.parent_id === null)
                .map((folder: IFolderTemplate, idx) => (
                  <FolderNode key={idx} folder={folder} allFolders={templateValue?.sections} subIndex={0} />
                ))}
            </div>
          ) : (
            <span className="import-folder__no-template">Choose a template from the drop-drown menu to continue</span>
          )}
          <div className="import-folder__loading">
            <Loading loading={importLoading} processTitle="Importing in progress" resultTitle="Done!" />
          </div>
        </div>
        <div className="import-folder__footer">
          <Button sx={{ marginRight: "20px" }} variant="outlined" onClick={closeSlider} disabled={importLoading}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await onSubmit();
              closeSlider();
            }}
            type="submit"
            variant="contained"
            disabled={importLoading || !templateValue || !state.folders.length}
          >
            Import
          </Button>
        </div>
      </ImportContext.Provider>
    </div>
  );
};
